/**
 * Sign-off ageing rules, shared by every sign-off surface in Nutrition.
 *
 * Extracted from soilSignOff.ts (U3) in F3 so foliar does not grow a THIRD
 * copy — spec §4.4 makes that explicit, and U3 had already unified two soil
 * modules whose comments each falsely claimed they could not disagree.
 * `soilSignOff` re-exports every symbol below, so its own consumers
 * (SoilSignOffPage, soilSessionDetail) are unchanged.
 *
 * Thresholds are constants, NOT tenant configuration: a tenant setting needs a
 * settings surface, a migration and a backend read, none of which exists. They
 * live here so a later slice can lift them to config without touching any page
 * (parent spec §10 open item 2; foliar adopts soil's answer rather than
 * picking a second set).
 *
 * `WaitingTone` is deliberately a SUBSET of StatusVariant
 * ('ok'|'warn'|'danger'|'info'|'queued'), so a tone can be handed straight to
 * <StatusPill variant={tone}>. The foliar spec §4.4 asks for a 'mut' tier;
 * no such variant exists and adding one is a design-system change.
 */
export type WaitingTone = 'info' | 'warn' | 'danger';

export const WAITING_WARN_DAYS = 3;
export const WAITING_DANGER_DAYS = 7;

const DAY_MS = 86400000;

export function waitingTone(days: number): WaitingTone {
  if (days >= WAITING_DANGER_DAYS) return 'danger';
  if (days >= WAITING_WARN_DAYS) return 'warn';
  return 'info';
}

/**
 * Whole days a completed item has been waiting — the single day-diff
 * implementation for the module.
 *
 * Clamped to 0: a completedAt in the future (tablet/server clock drift is real
 * in the field) must never render as a negative wait. A missing completedAt
 * would produce NaN, which renders as "NaN days" and sorts unpredictably, so
 * it degrades to 0 — visibly wrong beats silently broken. Callers that need to
 * distinguish "not ageing yet" from "aged 0 days" must decide that BEFORE
 * calling (both detail screens pass null through instead).
 */
export function waitingDaysSince(completedAt: string | null | undefined, now: Date): number {
  if (!completedAt) return 0;
  return Math.max(0, Math.floor((now.getTime() - new Date(completedAt).getTime()) / DAY_MS));
}
